import { useState } from 'react';
import { Button, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useForm, Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import transactionsServices from 'src/services/transactions/axios/transactionsServices';
import { SEARCH_TOLERANCE_MINUTES } from 'src/services/utils';
import { ISearchTransactionsCriteria, ITransactionsData } from 'src/interfaces/transactions';
import { useGetTransactionsMetadata } from 'src/hooks/transactions';
import TableListTransactions from 'src/components/TransactionsComponent/TableListTransactions';
import EmptyList from 'src/components/EmptyList';
import ErrorList from 'src/components/ErrorList';
import merchantImage from 'src/global/assets/images/stores-merchant.png';
import errorIcon from 'src/global/assets/icons/not-found.svg';

import {
  AMOUNT_MESSAGE,
  EMPTY_SEARCH_VALUES,
  parseAmount,
  METADATA_ERROR_MESSAGE,
  NOT_FOUND_MESSAGE,
  REQUIRED_MESSAGE,
  SEARCH_ERROR_MESSAGE,
  SearchFormValues
} from './constants';
import { styles } from './styles';

type SearchStatus = 'idle' | 'loading' | 'success' | 'error';

/**
 * Búsqueda exacta de un pago (H3) — contenido de la tab Consulta.
 * 4 campos (sin Sucursal en MVP), opciones dinámicas desde /transactions/metadata,
 * disparo on-submit contra /transactions/search (tolerancia ±10 min server-side).
 * Los criterios NO se reflejan en la URL (RF-01c): viven solo en el estado del form.
 */
const SearchTransactionComponent = () => {
  const { classes } = styles();
  const { data: metadataResult, isError: metadataError, isLoading: metadataLoading } = useGetTransactionsMetadata();
  const metadata = metadataResult?.data;

  const { control, handleSubmit } = useForm<SearchFormValues>({ defaultValues: EMPTY_SEARCH_VALUES });

  const [status, setStatus] = useState<SearchStatus>('idle');
  const [results, setResults] = useState<ITransactionsData[]>([]);
  const [lastCriteria, setLastCriteria] = useState<ISearchTransactionsCriteria | null>(null);

  const runSearch = async (criteria: ISearchTransactionsCriteria) => {
    setStatus('loading');
    setLastCriteria(criteria);
    try {
      const response = await transactionsServices.searchTransactions(criteria);
      setResults(response.data?.transactions ?? []);
      setStatus('success');
    } catch {
      setResults([]);
      setStatus('error');
    }
  };

  const onSubmit = handleSubmit((values) => {
    const criteria: ISearchTransactionsCriteria = {
      datetime: dayjs(values.datetime).toISOString(),
      amount: parseAmount(values.amount),
      payment_method: values.paymentMethod
    };
    if (values.bank) criteria.bank = values.bank;
    return runSearch(criteria);
  });

  const selectHelper = metadataError ? METADATA_ERROR_MESSAGE : undefined;

  return (
    <div className={classes.container}>
      <form aria-label="search-transaction-form" onSubmit={onSubmit} className={classes.form}>
        <div className={classes.grid}>
          <Controller
            control={control}
            name="datetime"
            rules={{ required: REQUIRED_MESSAGE }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <DateTimePicker
                disableFuture
                label="Fecha y hora"
                format="DD-MM-YYYY HH:mm"
                ampm={false}
                value={value ? dayjs(value) : null}
                onChange={onChange}
                slotProps={{
                  textField: { size: 'small', error: !!error, helperText: error?.message },
                  // Sin ancho propio el popup reparte el espacio del campo entre
                  // calendario y relojes, y las columnas de hora y minuto quedan
                  // tan angostas que los numeros se cortan y no se pueden tocar.
                  desktopPaper: { className: classes.pickerPaper }
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="amount"
            rules={{
              required: REQUIRED_MESSAGE,
              validate: (value) => parseAmount(value) > 0 || AMOUNT_MESSAGE
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Monto"
                size="small"
                // Campo de texto y no numerico: uno numerico no acepta el punto
                // de miles con el que el portal muestra los montos.
                inputProps={{ inputMode: 'decimal' }}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="paymentMethod"
            rules={{ required: REQUIRED_MESSAGE }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                select
                label="Medio de pago"
                size="small"
                disabled={metadataLoading || metadataError}
                error={!!error || metadataError}
                helperText={error?.message ?? selectHelper}>
                {(metadata?.card_types ?? []).map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            control={control}
            name="bank"
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Banco"
                size="small"
                disabled={metadataLoading || metadataError}
                error={metadataError}
                helperText={selectHelper}>
                <MenuItem value="">Todos</MenuItem>
                {(metadata?.banks ?? []).map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </div>
        <div className={classes.actions}>
          <Typography variant="caption" className={classes.hint}>
            {`La búsqueda tolera ±${SEARCH_TOLERANCE_MINUTES} minutos sobre la fecha y hora indicadas`}
          </Typography>
          <Button
            variant="contained"
            className={classes.submitButton}
            type="submit"
            disabled={status === 'loading'}>
            Buscar
          </Button>
        </div>
      </form>

      <div className={classes.results}>
        {status === 'loading' && (
          <div className={classes.feedback}>
            <CircularProgress size={32} />
          </div>
        )}

        {status === 'success' && results.length > 0 && <TableListTransactions transactions={results} />}

        {status === 'success' && results.length === 0 && (
          <EmptyList image={merchantImage} message={NOT_FOUND_MESSAGE} hasButton={false} />
        )}

        {status === 'error' && (
          <div className={classes.feedback}>
            <ErrorList title={SEARCH_ERROR_MESSAGE} image={errorIcon} />
            <Button variant="outlined" onClick={() => lastCriteria && runSearch(lastCriteria)}>
              Reintentar
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchTransactionComponent;
